import { useEffect, useId, useRef, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { Check, ChevronDown, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { FilterGroup, FilterSelection } from './useListingFilters'

const EASE_OUT = [0.23, 1, 0.32, 1] as const

interface FilterBarProps {
  groups: FilterGroup[]
  selection: FilterSelection
  onToggle: (groupId: string, value: string) => void
  onClear: () => void
  /** Live result summary, e.g. "3 of 5 stories". */
  resultLabel: string
  label?: string
  className?: string
}

interface FilterDropdownProps {
  group: FilterGroup
  selected: string[]
  open: boolean
  onOpenChange: (open: boolean) => void
  onToggle: (value: string) => void
}

function FilterDropdown({ group, selected, open, onOpenChange, onToggle }: FilterDropdownProps) {
  const reduce = useReducedMotion()
  const panelId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  // Close on outside press and on Escape (focus returns to the toggle).
  useEffect(() => {
    if (!open) return
    const onPointer = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) onOpenChange(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onOpenChange(false)
        buttonRef.current?.focus()
      }
    }
    document.addEventListener('pointerdown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, onOpenChange])

  return (
    <div ref={rootRef} className="sm:relative">
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => onOpenChange(!open)}
        className={cn(
          'inline-flex h-11 items-center gap-2 px-4 font-[family-name:var(--font-landing-subdisplay)] text-sm font-bold uppercase tracking-[0.02em]',
          'bg-[var(--color-landing-primary)]/5 text-[var(--color-landing-gray-900)] dark:bg-white/[0.07] dark:text-white',
          'transition-[background-color,transform] duration-150 ease-out hover:bg-[var(--color-landing-primary)]/10 dark:hover:bg-white/[0.12] active:scale-[0.97]',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-landing-accent)]'
        )}
      >
        {group.label}
        {selected.length > 0 && (
          <span className="inline-flex h-5 min-w-5 items-center justify-center bg-[var(--color-landing-accent)] px-1 text-[0.6875rem] text-white">
            {selected.length}
            <span className="sr-only"> selected</span>
          </span>
        )}
        <ChevronDown
          aria-hidden
          className={cn('h-4 w-4 transition-transform duration-200 ease-out', open && 'rotate-180')}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            id={panelId}
            role="group"
            aria-label={`${group.label} filters`}
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.16, ease: EASE_OUT }}
            style={{ transformOrigin: 'top left' }}
            className="absolute left-0 right-0 top-full z-30 mt-1 sm:right-auto sm:min-w-[15rem] border border-[var(--color-landing-gray-200)] bg-white p-1.5 shadow-[0_12px_32px_-12px_rgba(10,10,10,0.25)] dark:border-[var(--color-landing-gray-800)] dark:bg-[#12121a]"
          >
            {group.options.map((option) => {
              const checked = selected.includes(option.value)
              return (
                <label
                  key={option.value}
                  className="flex cursor-pointer items-center gap-3 px-3 py-2.5 text-sm text-[var(--color-landing-gray-800)] hover:bg-[var(--color-landing-gray-50)] dark:text-[var(--color-landing-gray-200)] dark:hover:bg-white/[0.06] has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-[var(--color-landing-accent)]"
                >
                  <input
                    type="checkbox"
                    className="peer sr-only"
                    checked={checked}
                    onChange={() => onToggle(option.value)}
                  />
                  <span
                    aria-hidden
                    className={cn(
                      'flex h-4 w-4 shrink-0 items-center justify-center border transition-colors duration-150',
                      checked
                        ? 'border-[var(--color-landing-accent)] bg-[var(--color-landing-accent)] text-white'
                        : 'border-[var(--color-landing-gray-400)] dark:border-[var(--color-landing-gray-600)]'
                    )}
                  >
                    {checked && <Check className="h-3 w-3" strokeWidth={3} />}
                  </span>
                  {option.label}
                </label>
              )
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

/**
 * The reference's listing filter row: a purple label box, dropdown toggles on
 * a 5% black fill, and a "clear filters" link. Multi-select per group.
 */
export function FilterBar({
  groups,
  selection,
  onToggle,
  onClear,
  resultLabel,
  label = 'Filter by',
  className,
}: FilterBarProps) {
  const [openId, setOpenId] = useState<string | null>(null)
  const hasSelection = Object.values(selection).some((values) => values.length > 0)

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <span className="landing-text-overline self-start bg-[var(--color-landing-secondary)] px-1.5 py-1 !text-[0.75rem] text-white">
        {label}
      </span>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
        <div className="relative flex flex-wrap gap-2">
          {groups.map((group) => (
            <FilterDropdown
              key={group.id}
              group={group}
              selected={selection[group.id] ?? []}
              open={openId === group.id}
              onOpenChange={(open) => setOpenId(open ? group.id : null)}
              onToggle={(value) => onToggle(group.id, value)}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={() => {
            setOpenId(null)
            onClear()
          }}
          disabled={!hasSelection}
          className="inline-flex items-center gap-1.5 text-sm text-[var(--color-landing-gray-900)]/65 underline-offset-4 transition-colors duration-150 hover:text-[var(--color-landing-gray-900)] hover:underline disabled:cursor-default disabled:no-underline disabled:opacity-50 disabled:hover:text-[var(--color-landing-gray-900)]/65 dark:text-white/65 dark:hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-landing-accent)]"
        >
          <X aria-hidden className="h-4 w-4" />
          Clear filters
        </button>
        <p
          aria-live="polite"
          className="text-sm text-[var(--color-landing-gray-900)]/65 dark:text-white/65 sm:ml-auto"
        >
          {resultLabel}
        </p>
      </div>
    </div>
  )
}
